import { prefersReducedMotion } from "./motion";
import { tabRootOf, TAB_PHONE_WIDTH } from "./tab_swipe";
import type { TabDirection } from "./tabs";

/** Loaded routes can cross-fade once their committed location is visible. */
const loaded = new Set(["/", "/home"]);
let pending: { path: string; resolve: () => void } | null = null;
export function markRouteLoaded(path: string): void { loaded.add(path); }
export function routeLoaded(path: string): boolean { return loaded.has(path); }
/**
 * The longest a cross-fade may hold the old frame. A view transition freezes
 * rendering until its callback settles, so a commit that never arrives must not
 * leave the screen frozen; past this the fade simply finishes on whatever is
 * there.
 */
export const ROUTE_COMMIT_TIMEOUT_MS = 500;
export function waitForRouteCommit(path: string): Promise<void> {
  pending?.resolve();
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      if (pending?.path === path) pending = null;
      resolve();
    }, ROUTE_COMMIT_TIMEOUT_MS);
    pending = { path, resolve: () => { clearTimeout(timer); resolve(); } };
  });
}
export function routeCommitted(path: string): void {
  if (pending?.path === path) { pending.resolve(); pending = null; }
}

let activeTabTransition: ViewTransition | null = null;
/** Loaded destinations can animate; fetching a route never holds the old screen. */
export function navigateTab(to: string, direction: TabDirection, navigate: (to: string) => void, from: string, beforeNavigate?: () => void): void {
  activeTabTransition?.skipTransition();
  activeTabTransition = null;
  pending?.resolve();
  pending = null;
  delete document.documentElement.dataset.navDirection;
  const phone = window.innerWidth < TAB_PHONE_WIDTH;
  if (to !== "/" && tabRootOf(from) && routeLoaded(to) && !prefersReducedMotion() && typeof document.startViewTransition === "function") {
    if (phone) document.documentElement.dataset.navDirection = direction;
    const transition = document.startViewTransition(() => {
      const committed = waitForRouteCommit(to);
      beforeNavigate?.();
      navigate(to);
      return committed;
    });
    activeTabTransition = transition;
    const cleanup = (): void => {
      if (activeTabTransition !== transition) return;
      activeTabTransition = null;
      delete document.documentElement.dataset.navDirection;
    };
    void transition.finished.then(cleanup, cleanup);
  } else { beforeNavigate?.(); navigate(to); }
}
